<template>
  <v-card flat class="elevation-0">
    <FormErrors :show="showResourceErrors" :errors="resourceErrors" />
    <v-data-table flat :headers="headers" :items="items" hide-default-footer>
      <template v-slot:item.actions="{ item }">
        <option-menu :item="item" :editItem="onEdit" :deleteItem="destroy" />
      </template>
    </v-data-table>
    <v-toolbar flat color="transparent">
      <v-spacer></v-spacer>
      <v-btn depressed color="accent" @click="create">
        {{ btnTitle }}
      </v-btn>
    </v-toolbar>
    <router-view></router-view>
  </v-card>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { NAMESPACE } from '../store'
import { BaseForm, FormErrors, OptionMenu } from '@argon/app/components'

export default {
  name: 'TransitionsList',
  extends: BaseForm,
  components: { FormErrors, OptionMenu },
  props: {
    flowId: {
      type: [String, Number],
      required: true
    },
    origin: { type: String, required: true },
    display: { type: String, required: true },
    ingress: { type: Boolean, default: false }
  },
  computed: {
    ...mapState(NAMESPACE, ['current']),
    transitions() {
      return this.current
        ? this.current.meta.transitions.filter((x) =>
            this.ingress ? x.to_state === this.origin : x.from_state === this.origin
          )
        : []
    },
    items() {
      return this.transitions.map((t) => {
        let stateName = this.ingress ? t.from_state : t.to_state
        let stateObj = this.current.meta.states.find((i) => i.name === stateName)
        let state = stateObj ? stateObj.display : stateName
        let actions = this.current.meta.actions
          .filter((i) => t.actions.includes(i.name))
          .map((i) => i.display)
          .join(', ')

        return {
          ...t,
          state,
          actions
        }
      })
    },
    headers() {
      return [
        {
          text: this.$t('displayName'),
          value: 'display'
        },
        {
          text: this.ingress ? this.$t('origin') : this.$t('destination'),
          value: 'state'
        },
        {
          value: 'actions',
          width: 20,
          sortable: false
        }
      ]
    },
    btnTitle() {
      return this.ingress ? this.$t('newIngress') : this.$t('newEgress')
    }
  },
  methods: {
    ...mapActions(NAMESPACE, ['deleteTransition']),
    getTransition(item) {
      return this.transitions.find((x) => x.name === item.name)
    },
    create() {
      this.$router.push({
        name: 'flowTransition',
        params: {
          ...this.$route.params,
          ingress: this.ingress,
          origin: this.origin,
          transitionName: 'new'
        }
      })
    },
    destroy(item) {
      this.resetServerErrors()
      let data = this.getTransition(item)
      let payload = { flowId: this.flowId, data }
      return this.deleteTransition(payload).catch(this.setServerErrors)
    },
    onEdit(item) {
      this.$router.push({
        name: 'flowTransition',
        params: {
          ...this.$route.params,
          ingress: this.ingress,
          origin: this.origin,
          transitionName: item.name
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
::v-deep .v-toolbar__content {
  padding: 0 !important;
}
</style>

<i18n>
{
  "en": {
    "actions": "@:labels.actions",
    "destination": "Destination",
    "displayName": "@:labels.displayName",
    "newEgress": "Add Outgoing Transition",
    "newIngress": "Add Incoming Transition",
    "origin": "Origin",
    "state": "@:labels.state"
  },
  "fr": {
    "actions": "@:labels.actions",
    "destination": "Destination",
    "displayName": "@:labels.displayName",
    "newEgress": "Ajouter une transition sortante",
    "newIngress": "Ajouter une transition entrante",
    "origin": "Origine",
    "state": "@:labels.state"
  }
}
</i18n>
